// ==========================================================================
// PhoneAuthGate - The pre-existing OTP/magic-link auth + route protection.
// Extracted from AuthProvider so the Telegram modality can plug in a
// parallel TelegramAuthGate without touching this code path.
// ==========================================================================

import { backgroundImage, weatherIcon } from '@/assets';
import { useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/hooks/useUser';
import { useAuthStore } from '@/stores/authStore';
import { getRequiredRoute, isPublicRoute } from './authRouting';

interface PhoneAuthGateProps {
  children: React.ReactNode;
}

export function PhoneAuthGate({ children }: PhoneAuthGateProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const hasToken = !!localStorage.getItem('access_token');

  const { data: user, isLoading, isError, error } = useUser(hasToken);
  const { logout } = useAuthStore();

  useEffect(() => {
    if (isError) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        logout();
        if (!isPublicRoute(location.pathname)) {
          navigate('/auth/phone', { replace: true });
        }
      }
    }
  }, [isError, error, logout, navigate, location.pathname]);

  if (hasToken && isLoading) {
    return <LoadingScreen />;
  }

  if (!hasToken && !isPublicRoute(location.pathname)) {
    return <Navigate to="/auth/phone" replace />;
  }

  if (user) {
    const requiredRoute = getRequiredRoute(user, location.pathname);
    if (requiredRoute) {
      return <Navigate to={requiredRoute} replace />;
    }
  }

  return <>{children}</>;
}

// Same loading screen shape as the original — used by both gates.
export function LoadingScreen() {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <img
        src={weatherIcon}
        alt="Loading"
        className="w-[140px] h-[140px] object-contain mb-8"
      />
      <div className="flex gap-1">
        <div className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}
